import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { SYS4010_R01Dto } from 'src/containers/mes/SYS/SYS4010_R01/Dto_SYS4010_R01';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { useSYS4010_R01Store } from './Store_SYS4010_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'SYS4010_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<SYS4010_R01Dto> | null>;
	defaultSYS4010_R01Row: SYS4010_R01Dto;
}

export const Table_SYS4010_R01 = (props: Props) => {
	const { gridRef, defaultSYS4010_R01Row } = props;

	const {
		SYS4010_R01List,
		selectedSYS4010_R01,
		setSelectedSYS4010_R01,
		initSelectedSYS4010_R01,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
	} = useSYS4010_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'compCd',
		gridData: SYS4010_R01List,
		defaultGridData: defaultSYS4010_R01Row,
		selectedRow: selectedSYS4010_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		compCd: '사업장코드',
		compNm: '상호',
		regNo: '사업자번호',
		represent: '대표자',
		telNo: '전화번호',
		faxNo: '팩스번호',
		taxMobile: '세금계산서 휴대폰',
		taxEmail: '세금계산서 email',
		address: '주소',
		detailAddress: '상세주소',
		remark: '비고',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compCd')(params), []);

	const baseColumnDefs: ColDef<SYS4010_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '사업장코드', colId: 'compCd', field: 'compCd', width: 110,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'compCd'),
		},
		{
			headerName: '상호', colId: 'compNm', field: 'compNm', width: 120,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
		},
		{
			headerName: '사업자번호', colId: 'regNo', field: 'regNo', width: 120,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'regNo'),
		},
		{
			headerName: '대표자', colId: 'represent', field: 'represent', width: 120,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'represent'),
		},
		{
			headerName: '전화번호', colId: 'telNo', field: 'telNo', width: 130,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'telNo'),
		},
		{
			headerName: '세금계산서 휴대폰', colId: 'taxMobile', field: 'taxMobile', width: 150,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'taxMobile'),
		},
		{
			headerName: '세금계산서 email', colId: 'taxEmail', field: 'taxEmail', width: 170,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'taxEmail'),
		},
		{
			headerName: '주소', colId: 'address', field: 'address', width: 240,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'address'),
		},
		{
			headerName: '상세주소', colId: 'detailAddress', field: 'detailAddress', width: 110,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'detailAddress'),
		},
		{
			headerName: '비고', colId: 'remark', field: 'remark',
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'remark'),
		},
	], [getCellStyle]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SYS4010_R01Dto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<SYS4010_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<SYS4010_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedSYS4010_R01?.compCd === rowData.compCd;
		setSelectedSYS4010_R01(isSelected ? undefined : rowData);
	}, [selectedSYS4010_R01, setSelectedSYS4010_R01]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		compCd: `${SYS4010_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [SYS4010_R01List.length]);

	useEffect(() => { ensureSelectedRow(); }, [SYS4010_R01List, selectedSYS4010_R01]);

	return (
		<GridContextWrapper<SYS4010_R01Dto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedSYS4010_R01}
			defaultRowData={defaultSYS4010_R01Row}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<SYS4010_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<SYS4010_R01Dto>({
					idKey: 'compCd',
					selectedList: selectedSYS4010_R01 ? [selectedSYS4010_R01.compCd] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
